/**
 * StatusUpdatesPage — consolidated, OM+-privileged feed of every status log
 * entry across Projects, Proposals, Leads, and RFPs.
 *
 * Filters: day, date range, project, record type, client, employee.
 * Each row shows the source record (clickable into its detail panel's Status
 * Log tab), the status update body, the action item (with a green "To-Do
 * created" link when one exists), the author, and the timestamp.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Filter, ChevronLeft, ChevronRight, ListTodo, Briefcase, FileText, ExternalLink, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import RichTextDisplay from '@/components/RichTextDisplay';

const PAGE_SIZE = 50;

const ENTITY_PILL = {
  project: { label: 'Project', cls: 'bg-primary/10 text-primary border-primary/20' },
  proposal: { label: 'Proposal', cls: 'bg-amber-500/10 text-amber-400 border-amber-500/20' },
  lead: { label: 'Lead', cls: 'bg-sky-500/10 text-sky-400 border-sky-500/20' },
  rfp: { label: 'RFP', cls: 'bg-purple-500/10 text-purple-400 border-purple-500/20' },
};

const RANGE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: '7d', label: 'Last 7 days' },
  { id: '30d', label: 'Last 30 days' },
  { id: 'mtd', label: 'Month to date' },
  { id: 'all', label: 'All time' },
  { id: 'custom', label: 'Custom range' },
];

function computeRange(preset) {
  const today = new Date();
  const fmt = (d) => d.toISOString().slice(0, 10);
  if (preset === 'today') return { from: fmt(today), to: fmt(today) };
  if (preset === '7d') {
    const f = new Date(today); f.setDate(f.getDate() - 6);
    return { from: fmt(f), to: fmt(today) };
  }
  if (preset === '30d') {
    const f = new Date(today); f.setDate(f.getDate() - 29);
    return { from: fmt(f), to: fmt(today) };
  }
  if (preset === 'mtd') {
    const f = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: fmt(f), to: fmt(today) };
  }
  return { from: '', to: '' };
}

function formatTimestamp(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    return d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
  } catch { return ts; }
}

export default function StatusUpdatesPage() {
  const { hasPerm } = useAuth();
  const canView = !!hasPerm('view_status_log_global');
  const navigate = useNavigate();

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filterOpts, setFilterOpts] = useState({ authors: [], projects: [], clients: [], entity_types: [] });

  // Filter form state
  const [rangePreset, setRangePreset] = useState('30d');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [entityType, setEntityType] = useState('all');
  const [projectId, setProjectId] = useState('all');
  const [clientId, setClientId] = useState('all');
  const [authorId, setAuthorId] = useState('all');

  // Seed the date pickers from the preset
  useEffect(() => {
    if (rangePreset === 'custom') return;
    const r = computeRange(rangePreset);
    setDateFrom(r.from);
    setDateTo(r.to);
  }, [rangePreset]);

  const fetchFilters = useCallback(async () => {
    try {
      const r = await api.get('/status-updates/filters');
      setFilterOpts(r.data);
    } catch (e) {
      toast.error('Failed to load filter options');
    }
  }, []);

  const fetchData = useCallback(async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (entityType !== 'all') params.entity_type = entityType;
      if (projectId !== 'all') params.project_id = projectId;
      if (clientId !== 'all') params.client_id = clientId;
      if (authorId !== 'all') params.created_by = authorId;
      const r = await api.get('/status-updates', { params });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load status updates');
    } finally {
      setLoading(false);
    }
  }, [canView, page, dateFrom, dateTo, entityType, projectId, clientId, authorId]);

  useEffect(() => { if (canView) fetchFilters(); }, [canView, fetchFilters]);
  useEffect(() => { fetchData(); }, [fetchData]);

  // Reset to first page whenever any filter (other than page) changes
  useEffect(() => { setPage(0); }, [dateFrom, dateTo, entityType, projectId, clientId, authorId]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const resetFilters = () => {
    setRangePreset('30d');
    setEntityType('all');
    setProjectId('all');
    setClientId('all');
    setAuthorId('all');
  };

  const navigateToEntity = (item) => {
    if (!item.entity_route) return;
    // Project routes carry deep-link params; lead/proposal/rfp routes open
    // the index page where the user can pick the row.
    navigate(item.entity_route);
  };

  // ── Visible row count display (excludes filtered-by-permission misses)
  const summary = useMemo(() => {
    if (total === 0) return '0 entries';
    const start = page * PAGE_SIZE + 1;
    const end = Math.min(total, (page + 1) * PAGE_SIZE);
    return `${start}-${end} of ${total} entries`;
  }, [page, total]);

  if (!canView) {
    return (
      <div className="p-6 max-w-2xl mx-auto" data-testid="status-updates-no-perm">
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-6">
            <p className="text-sm text-foreground">You don&apos;t have permission to view the consolidated Status Updates feed.</p>
            <p className="text-xs text-muted-foreground mt-2">Ask an administrator to enable the &quot;view status log (all records)&quot; permission on your account if you need access.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4 p-4 sm:p-6" data-testid="status-updates-page">
      <header className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading">Status Updates</h1>
          <p className="text-sm text-muted-foreground mt-0.5">A single feed of every status update across Projects, Proposals, Leads, and RFPs.</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} disabled={loading} data-testid="status-updates-refresh-btn">
          <RotateCw className={`h-3.5 w-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </header>

      {/* Filters */}
      <Card className="border-border bg-card">
        <CardContent className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <p className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">Filters</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <Label className="text-xs text-muted-foreground">Range</Label>
              <Select value={rangePreset} onValueChange={setRangePreset}>
                <SelectTrigger className="bg-background border-border" data-testid="su-range-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {RANGE_PRESETS.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">From</Label>
              <Input type="date" value={dateFrom} onChange={e => { setRangePreset('custom'); setDateFrom(e.target.value); }} className="bg-background border-border" data-testid="su-date-from" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">To</Label>
              <Input type="date" value={dateTo} onChange={e => { setRangePreset('custom'); setDateTo(e.target.value); }} className="bg-background border-border" data-testid="su-date-to" />
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Record Type</Label>
              <Select value={entityType} onValueChange={setEntityType}>
                <SelectTrigger className="bg-background border-border" data-testid="su-entity-type-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="all">All types</SelectItem>
                  <SelectItem value="project">Project</SelectItem>
                  <SelectItem value="proposal">Proposal</SelectItem>
                  <SelectItem value="lead">Lead</SelectItem>
                  <SelectItem value="rfp">RFP</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Project</Label>
              <Select value={projectId} onValueChange={setProjectId}>
                <SelectTrigger className="bg-background border-border" data-testid="su-project-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-72">
                  <SelectItem value="all">All projects</SelectItem>
                  {filterOpts.projects.map(p => <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Client</Label>
              <Select value={clientId} onValueChange={setClientId}>
                <SelectTrigger className="bg-background border-border" data-testid="su-client-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-72">
                  <SelectItem value="all">All clients</SelectItem>
                  {filterOpts.clients.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-muted-foreground">Employee</Label>
              <Select value={authorId} onValueChange={setAuthorId}>
                <SelectTrigger className="bg-background border-border" data-testid="su-author-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border max-h-72">
                  <SelectItem value="all">All employees</SelectItem>
                  {filterOpts.authors.map(a => <SelectItem key={a.id} value={a.id}>{a.name || 'Unknown'}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={resetFilters} className="text-muted-foreground" data-testid="su-reset-filters-btn">Reset filters</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      <Card className="border-border bg-card">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-xs px-3 w-[160px]">Date / Time</TableHead>
                <TableHead className="text-xs px-3 w-[110px]">Type</TableHead>
                <TableHead className="text-xs px-3 min-w-[180px]">Record</TableHead>
                <TableHead className="text-xs px-3">Status Update</TableHead>
                <TableHead className="text-xs px-3 min-w-[180px]">Action Item / To-Do</TableHead>
                <TableHead className="text-xs px-3 w-[140px]">Author</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-8">Loading status updates...</TableCell></TableRow>
              ) : items.length === 0 ? (
                <TableRow><TableCell colSpan={6} className="text-center text-xs text-muted-foreground py-8">No status updates match the current filters.</TableCell></TableRow>
              ) : items.map(item => {
                const pill = ENTITY_PILL[item.entity_type] || { label: item.entity_type, cls: 'bg-secondary text-muted-foreground' };
                const IconEl = item.entity_type === 'project' ? Briefcase : FileText;
                return (
                  <TableRow key={item.id} data-testid={`status-update-row-${item.id}`}>
                    <TableCell className="text-xs text-muted-foreground tabular-nums px-3 whitespace-nowrap align-top">{formatTimestamp(item.created_at)}</TableCell>
                    <TableCell className="px-3 align-top">
                      <Badge className={`text-xs whitespace-nowrap ${pill.cls}`}>{pill.label}</Badge>
                    </TableCell>
                    <TableCell className="px-3 align-top">
                      <button
                        type="button"
                        onClick={() => navigateToEntity(item)}
                        className="inline-flex items-center gap-1 text-xs text-foreground hover:text-primary hover:underline text-left max-w-full"
                        data-testid={`status-update-record-${item.id}`}
                        title={item.entity_label || ''}
                      >
                        <IconEl className="h-3 w-3 shrink-0" />
                        <span className="truncate">{item.entity_label || '(unlinked)'}</span>
                        <ExternalLink className="h-3 w-3 shrink-0 opacity-60" />
                      </button>
                      {item.client_name && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">Client: {item.client_name}</p>
                      )}
                    </TableCell>
                    <TableCell className="px-3 align-top max-w-[420px]">
                      <div className="text-xs">
                        <RichTextDisplay value={item.status_text} collapsedHeight={40} />
                      </div>
                    </TableCell>
                    <TableCell className="px-3 align-top">
                      {item.action_items ? (
                        <div className="space-y-1">
                          <p className="text-xs text-amber-400">{item.action_items}</p>
                          {item.linked_todo ? (
                            <Badge
                              className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 text-xs gap-1 cursor-pointer"
                              onClick={() => navigate('/tasks')}
                              data-testid={`status-update-todo-${item.id}`}
                            >
                              <ListTodo className="h-3 w-3" /> To-Do: {item.linked_todo.status}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-xs text-muted-foreground gap-1">
                              <ListTodo className="h-3 w-3" /> Suggestion on To-Dos
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/60 italic">-</span>
                      )}
                    </TableCell>
                    <TableCell className="px-3 text-xs align-top whitespace-nowrap">{item.created_by_name || '-'}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagination */}
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span className="tabular-nums">{summary}</span>
        {totalPages > 1 && (
          <div className="flex gap-1">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)} data-testid="su-prev-page">
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)} data-testid="su-next-page">
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
